/**
 * LateComersList - Displays a list of late arrivals or early departures
 */

import React from 'react';
import { Clock, AlertCircle, User, Calendar } from 'lucide-react';

const STATUS_CONFIG = {
  late: {
    icon: Clock,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-100',
    label: 'Late Arrival',
  },
  early: {
    icon: AlertCircle,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-100',
    label: 'Early Departure',
  },
};

/**
 * LateComersList Component
 * @param {Object} props
 * @param {Array} props.data - Array of late arrival/early departure records
 * @param {boolean} props.isLoading - Loading state
 * @param {string} props.type - 'late' | 'early'
 * @param {boolean} props.showClass - Whether to show class name
 * @param {boolean} props.compact - Compact view (fewer details)
 */
const LateComersList = ({
  data = [],
  isLoading = false,
  type = 'late',
  showClass = true,
  compact = false,
}) => {
  const config = STATUS_CONFIG[type];
  const Icon = config.icon;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Icon className="mx-auto h-12 w-12 text-gray-300 mb-3" />
        <p>No {type === 'late' ? 'late arrivals' : 'early departures'} found</p>
        <p className="text-sm text-gray-400 mt-1">
          {type === 'late'
            ? 'Students who arrive after the scheduled start time will appear here'
            : 'Students who leave before the scheduled end time will appear here'}
        </p>
      </div>
    );
  }

  const formatTime = (time) => {
    if (!time) return '-';
    const date = new Date(time);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getMinutesDiff = (actual, scheduled) => {
    if (!actual || !scheduled) return null;
    const actualTime = new Date(actual).getTime();
    const scheduledTime = new Date(scheduled).getTime();
    const diffMs = actualTime - scheduledTime;
    return Math.round(diffMs / 60000);
  };

  return (
    <div className="space-y-3">
      {data.map((record, index) => {
        const minutesDiff = type === 'late'
          ? getMinutesDiff(record.checkInTime, record.scheduledStartTime)
          : getMinutesDiff(record.scheduledEndTime, record.checkOutTime);

        return (
          <div
            key={record.id || index}
            className={`${config.bgColor} ${config.borderColor} border rounded-lg p-4 transition-all hover:shadow-sm`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                {/* Avatar */}
                <div className={`w-10 h-10 rounded-full ${config.bgColor} ${config.color} flex items-center justify-center flex-shrink-0`}>
                  <User size={20} />
                </div>

                {/* Student Info */}
                <div>
                  <h4 className="font-medium text-gray-900">
                    {record.studentName || record.student?.name || 'Unknown Student'}
                  </h4>
                  
                  {showClass && (record.className || record.class?.name) && (
                    <p className="text-sm text-gray-500 mt-0.5">
                      {record.className || record.class?.name}
                    </p>
                  )}

                  {!compact && (
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                      <span className="flex items-center">
                        <Calendar size={14} className="mr-1" />
                        {formatDate(record.date)}
                      </span>
                      {record.reason && (
                        <span className="text-gray-400">Reason: {record.reason}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Time Info */}
              <div className="text-right">
                <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
                  <Icon size={12} className="mr-1" />
                  {config.label}
                </div>

                <div className="mt-2 space-y-1">
                  {type === 'late' ? (
                    <>
                      <p className="text-sm text-gray-600">
                        <span className="text-gray-400">Scheduled:</span>{' '}
                        {formatTime(record.scheduledStartTime)}
                      </p>
                      <p className="text-sm text-gray-900 font-medium">
                        <span className="text-gray-400">Arrived:</span>{' '}
                        {formatTime(record.checkInTime)}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600">
                        <span className="text-gray-400">Scheduled End:</span>{' '}
                        {formatTime(record.scheduledEndTime)}
                      </p>
                      <p className="text-sm text-gray-900 font-medium">
                        <span className="text-gray-400">Left:</span>{' '}
                        {formatTime(record.checkOutTime)}
                      </p>
                    </>
                  )}

                  {minutesDiff !== null && minutesDiff > 0 && (
                    <p className={`text-sm font-medium ${type === 'late' ? 'text-yellow-600' : 'text-orange-600'}`}>
                      {minutesDiff} min {type === 'late' ? 'late' : 'early'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {record.notes && !compact && (
              <div className="mt-3 pt-3 border-t border-gray-200/50">
                <p className="text-sm text-gray-500">
                  <span className="font-medium">Notes:</span> {record.notes}
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * LateComersCompact - Compact version for dashboard widgets
 */
export const LateComersCompact = ({ data = [], isLoading = false, type = 'late', maxItems = 5 }) => {
  const config = STATUS_CONFIG[type];
  const Icon = config.icon;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <Icon className="mx-auto h-8 w-8 text-gray-300 mb-2" />
        <p className="text-sm">No {type === 'late' ? 'late arrivals' : 'early departures'}</p>
      </div>
    );
  }

  const formatTime = (time) => {
    if (!time) return '-';
    const date = new Date(time);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const displayData = data.slice(0, maxItems);

  return (
    <div className="space-y-2">
      {displayData.map((record, index) => (
        <div
          key={record.id || index}
          className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg"
        >
          <div className="flex items-center space-x-3">
            <div className={`w-8 h-8 rounded-full ${config.bgColor} ${config.color} flex items-center justify-center`}>
              <User size={16} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                {record.studentName || record.student?.name || 'Unknown'}
              </p>
              <p className="text-xs text-gray-500">
                {record.className || record.class?.name}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium text-gray-900">
              {type === 'late'
                ? formatTime(record.checkInTime)
                : formatTime(record.checkOutTime)}
            </p>
            <p className={`text-xs ${config.color}`}>
              {type === 'late' ? 'Late' : 'Early'}
            </p>
          </div>
        </div>
      ))}
      
      {data.length > maxItems && (
        <p className="text-center text-sm text-gray-500 py-2">
          +{data.length - maxItems} more
        </p>
      )}
    </div>
  );
};

export default LateComersList;
